
import React, { useEffect, useState, useCallback } from 'react';
import {
  ReactFlow,
  Controls,
  Background,
  Node,
  Edge,
  useNodesState,
  useEdgesState,
  ConnectionLineType
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Users } from "lucide-react";

interface AIRecommendationFlowProps {
  recommendation: any;
}

const AIRecommendationFlow = ({ recommendation }: AIRecommendationFlowProps) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => {
    if (recommendation?.organizationStructure) {
      const flowNodes = createOrganizationNodes(recommendation.organizationStructure);
      const flowEdges = createOrganizationEdges(recommendation.organizationStructure);
      
      setNodes(flowNodes);
      setEdges(flowEdges);
    }
  }, [recommendation, setNodes, setEdges]);

  const createOrganizationNodes = (structure: any): Node[] => {
    if (!structure.nodes) return [];

    // Group nodes by role type
    const nodesByType = {
      'project-director': structure.nodes.filter((n: any) => n.roleType === 'project-director'),
      'project-manager': structure.nodes.filter((n: any) => n.roleType === 'project-manager'),
      'team-lead': structure.nodes.filter((n: any) => n.roleType === 'team-lead'),
      'team-member': structure.nodes.filter((n: any) => n.roleType === 'team-member')
    };

    const enhancedNodes: Node[] = [];
    const nodeWidth = 240;
    const nodeHeight = 100;
    const horizontalSpacing = 20;
    const verticalSpacing = 120;
    const maxNodesPerRow = 15;
    
    let currentY = 50;

    // Process each node type in hierarchy order
    Object.entries(nodesByType).forEach(([roleType, nodesOfType]) => {
      if (nodesOfType.length === 0) return;

      const rows = Math.ceil(nodesOfType.length / maxNodesPerRow);
      
      for (let row = 0; row < rows; row++) {
        const startIndex = row * maxNodesPerRow;
        const endIndex = Math.min(startIndex + maxNodesPerRow, nodesOfType.length);
        const nodesInRow = nodesOfType.slice(startIndex, endIndex);
        
        const totalRowWidth = (nodesInRow.length * nodeWidth) + ((nodesInRow.length - 1) * horizontalSpacing);
        const startX = -totalRowWidth / 2;
        
        nodesInRow.forEach((node: any, index: number) => {
          const x = startX + (index * (nodeWidth + horizontalSpacing));
          const y = currentY + (row * verticalSpacing);
          
          enhancedNodes.push(createFlowNode(
            { ...node, position: { x, y } }, 
            { x, y }, 
            node.roleType
          ));
        });
      }
      
      // Move to next level with spacing
      currentY += (rows * verticalSpacing) + 80;
    });

    return enhancedNodes;
  };

  const createOrganizationEdges = (structure: any): Edge[] => {
    if (!structure.edges) return [];

    return structure.edges.map((edge: any) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      type: 'smoothstep',
      animated: false,
      style: { 
        stroke: '#6366f1', 
        strokeWidth: 2 
      },
      markerEnd: {
        type: 'arrowclosed' as const,
        color: '#6366f1'
      }
    }));
  };

  const createFlowNode = (node: any, position: { x: number; y: number }, roleType: string): Node => {
    let nodeStyle: React.CSSProperties;

    // Enhanced styling with better spacing
    switch(roleType) {
      case 'project-director':
        nodeStyle = {
          background: 'linear-gradient(135deg, #8B5CF6, #A855F7)',
          color: 'white',
          border: '2px solid #7C3AED',
          borderRadius: '12px',
          padding: '16px',
          width: '240px',
          height: '100px',
          boxShadow: '0 8px 25px rgba(139, 92, 246, 0.3)',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: '12px',
          lineHeight: '1.2'
        };
        break;
      case 'project-manager':
        nodeStyle = {
          background: 'linear-gradient(135deg, #3B82F6, #6366F1)',
          color: 'white',
          border: '2px solid #2563EB',
          borderRadius: '12px',
          padding: '14px',
          width: '220px',
          height: '90px',
          boxShadow: '0 6px 20px rgba(59, 130, 246, 0.3)',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: '11px',
          lineHeight: '1.2'
        };
        break;
      case 'team-lead':
        nodeStyle = {
          background: 'linear-gradient(135deg, #10B981, #059669)',
          color: 'white',
          border: '2px solid #047857',
          borderRadius: '12px',
          padding: '12px',
          width: '200px',
          height: '80px',
          boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: '10px',
          lineHeight: '1.2'
        };
        break;
      default:
        nodeStyle = {
          background: 'linear-gradient(135deg, #F59E0B, #D97706)',
          color: 'white',
          border: '2px solid #B45309',
          borderRadius: '12px',
          padding: '10px',
          width: '180px',
          height: '70px',
          boxShadow: '0 2px 10px rgba(245, 158, 11, 0.3)',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: '9px',
          lineHeight: '1.2'
        };
        break;
    }

    return {
      id: node.id,
      type: 'default',
      position: position,
      data: {
        label: (
          <div style={{ fontSize: 'inherit', lineHeight: 'inherit', textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: 'inherit' }}>
              {node.name}
            </div>
            <div style={{ fontSize: '90%', opacity: 0.9 }}>
              {node.role}
            </div>
          </div>
        ),
        role_type: node.roleType,
        resource_name: node.name
      },
      style: nodeStyle,
      className: `${roleType}-node custom-org-node`,
      draggable: false,
      selectable: true
    };
  };

  const handleNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    console.log('Organization node clicked:', node);
  }, []);

  // Calculate team statistics
  const teamStats = recommendation?.organizationStructure?.nodes ? {
    directors: recommendation.organizationStructure.nodes.filter((n: any) => n.roleType === 'project-director').length,
    managers: recommendation.organizationStructure.nodes.filter((n: any) => n.roleType === 'project-manager').length,
    teamLeads: recommendation.organizationStructure.nodes.filter((n: any) => n.roleType === 'team-lead').length,
    teamMembers: recommendation.organizationStructure.nodes.filter((n: any) => n.roleType === 'team-member').length
  } : { directors: 0, managers: 0, teamLeads: 0, teamMembers: 0 };

  return (
    <Card className="bg-secondary/50 backdrop-blur-lg border-2 border-primary">
      <CardHeader>
        <CardTitle className="text-primary flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          AI-Recommended Organization Structure - Optimized Team Layout
        </CardTitle>
        <div className="text-sm text-gray-300 mt-2">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-lg font-bold text-purple-300">{teamStats.directors}</div>
              <div className="text-xs text-gray-400">Project Directors</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-blue-300">{teamStats.managers}</div>
              <div className="text-xs text-gray-400">Project Managers</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-green-300">{teamStats.teamLeads}</div>
              <div className="text-xs text-gray-400">Team Leads</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-orange-300">{teamStats.teamMembers}</div>
              <div className="text-xs text-gray-400">Team Members</div>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {recommendation?.organizationStructure?.description && (
          <div className="mb-4 p-3 bg-primary/10 rounded-lg border border-primary/20">
            <p className="text-gray-300 text-sm">{recommendation.organizationStructure.description}</p>
          </div>
        )}
        
        <div style={{ height: 600, width: '100%' }}>
          <style>
            {`
              .custom-org-node {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              }
              .custom-org-node:hover {
                transform: scale(1.02);
                transition: transform 0.2s ease;
              }
              .custom-org-node.selected {
                box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.5) !important;
              }
            `}
          </style>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={handleNodeClick}
            fitView
            fitViewOptions={{
              padding: 0.2,
              minZoom: 0.4,
              maxZoom: 1.0
            }}
            connectionLineType={ConnectionLineType.SmoothStep}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={true}
            panOnDrag={true}
            zoomOnScroll={true}
            preventScrolling={false}
            minZoom={0.2}
            maxZoom={1.5}
          >
            <Controls />
            <Background color="#334155" gap={20} size={1} />
          </ReactFlow>
        </div>
      </CardContent>
    </Card>
  );
};

export default AIRecommendationFlow;
